import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@wa/ui";

/**
 * The sideways-scrolling container a kanban board sits in, with a gradient fade
 * at its edges.
 *
 * The gradient appears ONLY on the side that still has something behind it, the
 * way a shop's product slider behaves - unlike a static mask fade, which blurred
 * both edges even when there was nothing left to scroll to.
 *
 * `deps` is the data whose change re-measures the box (a column's contents
 * arriving later change its `scrollWidth`).
 */
export function ScrollFade({ children, className, deps = [] }: {
  children: ReactNode;
  className?: string;
  deps?: unknown[];
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [left, setLeft] = useState(false);
  const [right, setRight] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    function update() {
      if (!el) return;
      setLeft(el.scrollLeft > 1);
      setRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    }
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => { el.removeEventListener("scroll", update); ro.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return (
    <div className="relative">
      <div
        ref={ref}
        className={cn(
          // The scrollbar is hidden: the gradient fade is already the hint that
          // there is more to the side.
          "overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          className,
        )}
      >
        {children}
      </div>
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-background to-transparent transition-opacity duration-200",
          left ? "opacity-100" : "opacity-0",
        )}
      />
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-background to-transparent transition-opacity duration-200",
          right ? "opacity-100" : "opacity-0",
        )}
      />
    </div>
  );
}
